import React from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

export interface ISparkAreaChartProps {
    data: any;
    height?: number;
    width?: number;
    tooltipTitle?: string;
    tooltipValueFormatter?: (value: number) => string;
    key?: string;
    color?: string;
    fillColor?: string;
    showDates?: boolean;
    dateFormatter?: (date: string | Date) => string;
}

const SparkAreaChart: React.FC<ISparkAreaChartProps> = ({
    data,
    height = 60,
    width = 120,
    tooltipTitle = "Value",
    tooltipValueFormatter = (val) => val?.toString() || "0",
    key = "sparkline-chart",
    color = "#1890ff",
    fillColor = "rgba(24, 144, 255, 0.1)",
    showDates = false,
    dateFormatter = (date) => {
        if (!date) return "Invalid Date";
        if (typeof date === 'string') {
            const parsedDate = new Date(date);
            return isNaN(parsedDate.getTime()) ? "Invalid Date" : parsedDate.toLocaleDateString();
        }
        return date.toLocaleDateString?.() || "Invalid Date";
    },
}) => {
    const title = tooltipTitle || "Value";

    // Transform data to include dates if provided
    const transformedData = (data || []).map((item: any, index: number) => {
        if (item && typeof item === 'object' && item.date) {
            const timestamp = new Date(item.date).getTime();
            return [isNaN(timestamp) ? index : timestamp, item.value || 0];
        }
        return [index, item || 0];
    });

    const options = {
        chart: {
            type: "areaspline",
            height: height || 60,
            width: width || 120,
            backgroundColor: "transparent",
            margin: [0, 0, 0, 0],
        },
        credits: { enabled: false },
        title: { text: "" },
        xAxis: { 
            visible: false,
            type: showDates ? 'datetime' : 'linear'
        },
        yAxis: { visible: false },
        legend: { enabled: false },
        tooltip: {
            enabled: true,
            formatter: function (
                this: Highcharts.TooltipFormatterContextObject
            ): string {
                const xValue = this.x ?? "";
                const yValue = this.y ?? 0;
                let dateDisplay = "";
                
                if (showDates && typeof xValue === 'number') {
                    const date = new Date(xValue);
                    dateDisplay = isNaN(date.getTime()) ? "Invalid Date" : dateFormatter(date);
                } else {
                    dateDisplay = `Data point: ${xValue || 0}`;
                }
                
                return `
          <div style="font-size: 9px; color: #333;">
            <b>${title}</b><br/>
            <b>Date: ${dateDisplay}</b><br/>
            ${tooltipValueFormatter(yValue)}
          </div>
        `;
            },
            useHTML: true,
            backgroundColor: "#ffffff",
            borderRadius: 8,
            borderColor: "#ccc",
            borderWidth: 1,
            shadow: true,
            padding: 4,
        },
        plotOptions: {
            areaspline: {
                marker: { enabled: false },
                lineWidth: 2,
                states: {
                    hover: {
                        lineWidth: 2,
                    },
                },
                fillOpacity: 0.1,
            },
        },
        series: [
            {
                data: transformedData,
                color: color || "#1890ff",
                fillColor: fillColor || "rgba(24, 144, 255, 0.1)",
                type: "areaspline",
                threshold: null as number | undefined,
                connectNulls: true,
            },
        ],
    };

    return (
        <HighchartsReact highcharts={Highcharts} options={options} key={key} />
    );
};

export default SparkAreaChart;